// src/App.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { LoginPage } from './components/Auth/LoginPage';
import { UnlockPage } from './components/Auth/UnlockPage';
import { MainLayout } from './components/Layout/MainLayout';
import { NoteEditor } from './components/Notes/NoteEditor';
import { SettingsModal } from './components/Settings/SettingsModal';
import { ShareDialog } from './components/Notes/ShareDialog';
import { ShareList } from './components/Notes/ShareList';
import { useNotes } from './hooks/useNotes';
import { useNotifications } from './hooks/useNotifications';
import type { NoteType } from './types';
import { X } from 'lucide-react';
import { SharedNoteViewer } from './components/Notes/SharedNoteViewer';
import { startAuthentication } from '@simplewebauthn/browser';

const AUTO_LOCK_TIMEOUT = 5 * 60 * 1000;
// const AUTO_LOCK_TIMEOUT = 1 * 5 * 1000;

// ==========================================
// 輔助函數：CryptoKey 序列化/反序列化
// ==========================================

const serializeCryptoKey = async (key: CryptoKey): Promise<string> => {
  try {
    const exported = await crypto.subtle.exportKey('raw', key);
    const bytes = new Uint8Array(exported);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  } catch {
    return '';
  }
};

const deserializeCryptoKey = async (base64: string): Promise<CryptoKey | null> => {
  if (!base64) return null;
  try {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return await crypto.subtle.importKey(
      'raw',
      bytes,
      { name: 'AES-GCM' },
      true,
      ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
    );
  } catch {
    return null;
  }
};

function App() {
  // ==========================================
  // 從 sessionStorage 恢復登入狀態
  // ==========================================
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('auth_authenticated') === 'true';
  });
  
	// ==========================================
	// 1. 修改 isLocked 初始化（讀取 sessionStorage）
	// ==========================================
	const [isLocked, setIsLocked] = useState(() => {
		return sessionStorage.getItem('auth_isLocked') === 'true';
	});

	// 輔助函數：統一更新鎖定狀態
	const updateLockState = (locked: boolean) => {
		setIsLocked(locked);
		if (locked) {
			sessionStorage.setItem('auth_isLocked', 'true');
		} else {
			sessionStorage.removeItem('auth_isLocked');
		}
	};
  
  const [userId, setUserId] = useState<string>(() => {
    return sessionStorage.getItem('auth_userId') || '';
  });
  
  const [username, setUsername] = useState<string>(() => {
    return sessionStorage.getItem('auth_username') || '';
  });
  
  const [masterPassword, setMasterPassword] = useState<string>(() => {
    return sessionStorage.getItem('auth_masterPassword') || '';
  });
  
  const [lockPassword, setLockPassword] = useState<string>(() => {
    return sessionStorage.getItem('auth_lockPassword') || '';
  });
  
  const [dataEncryptionKey, setDataEncryptionKey] = useState<CryptoKey | null>(null);
  const [isRestoring, setIsRestoring] = useState(true);

  // ==========================================
  // 非同步初始化 DEK
  // ==========================================
  useEffect(() => {
    const savedDEK = sessionStorage.getItem('auth_dek');
    const savedAuth = sessionStorage.getItem('auth_authenticated');
    
    if (savedAuth === 'true' && savedDEK) {
      deserializeCryptoKey(savedDEK)
        .then(key => {
          if (key) {
            console.log('✅ DEK 恢復成功');
            setDataEncryptionKey(key);
          } else {
            console.warn('⚠️ DEK 恢復失敗');
            sessionStorage.removeItem('auth_authenticated');
            sessionStorage.removeItem('auth_dek');
            setIsAuthenticated(false);
          }
          setIsRestoring(false);
        })
        .catch(err => {
          console.warn('⚠️ DEK 恢復錯誤:', err);
          sessionStorage.removeItem('auth_authenticated');
          sessionStorage.removeItem('auth_dek');
          setIsAuthenticated(false);
          setIsRestoring(false);
        });
    } else {
      setIsRestoring(false);
    }
  }, []);

  // ==========================================
  // 其他狀態
  // ==========================================
  const [editingNote, setEditingNote] = useState<any>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<NoteType>('memo');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // 分享相關
  const [shareNote, setShareNote] = useState<any>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isShareListOpen, setIsShareListOpen] = useState(false);
  const [shareListTab, setShareListTab] = useState<'received' | 'shared'>('received');
  
  const [viewingNoteId, setViewingNoteId] = useState<string | null>(null);

  const handleViewSharedNote = (noteId: string) => {
    setViewingNoteId(noteId);
  };

  // 通知
  const { 
    notifications, 
    unreadCount, 
    receivedTotal, 
    receivedAccepted, 
    receivedPendingCount,
    myShareCount, 
    myAcceptedCount, 
    sharedPendingCount,
    markAsRead, 
    refresh,
    clearNotifiedIds,
  } = useNotifications(isAuthenticated ? userId : null);

  const { notes, loading, createNote, deleteNote, updateNote, togglePin, toggleArchive, loadNotes } = useNotes(masterPassword, dataEncryptionKey, userId);

  const lockTimerRef = useRef<number | null>(null);

  // ==========================================
  // 監聽分享列表事件
  // ==========================================
  useEffect(() => {
    const handleOpenShareList = (e: any) => {
      setShareListTab(e.detail?.tab || 'received');
      setIsShareListOpen(true);
    };
    window.addEventListener('openShareList', handleOpenShareList);
    return () => {
      window.removeEventListener('openShareList', handleOpenShareList);
    };
  }, []);

	// ==========================================
	// 2. 修改自動鎖定邏輯
	// ==========================================
	const resetLockTimer = () => {
		if (lockTimerRef.current) {
			clearTimeout(lockTimerRef.current);
		}
		if (isAuthenticated && !isLocked) {
			lockTimerRef.current = setTimeout(() => {
				updateLockState(true); // 使用持久化鎖定
			}, AUTO_LOCK_TIMEOUT);
		}
	};

  useEffect(() => {
    if (!isAuthenticated || isLocked) return;

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    const handleActivity = () => resetLockTimer();

    events.forEach(event => document.addEventListener(event, handleActivity));
    resetLockTimer();

    return () => {
      events.forEach(event => document.removeEventListener(event, handleActivity));
      if (lockTimerRef.current) {
        clearTimeout(lockTimerRef.current);
      }
    };
  }, [isAuthenticated, isLocked]);

  // ==========================================
  // 返回鍵攔截
  // ==========================================
  useEffect(() => {
    const handlePopState = (e: PopStateEvent) => {
      if (isAuthenticated) {
        window.history.pushState(null, '', window.location.href);
        toast.info('請使用應用內的返回功能', { icon: 'ℹ️' });
        e.preventDefault();
      }
    };

    if (isAuthenticated) {
      window.history.pushState(null, '', window.location.href);
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAuthenticated]);

	// ==========================================
	// 3. 修改 handleUnlock（支援密碼/指紋驗證解鎖）
	// ==========================================
	const handleUnlock = async (passwordInput?: string) => {
		// ==========================================
		// 1. 密碼解鎖路徑
		// ==========================================
		if (passwordInput && passwordInput.trim().length > 0) {
			try {
				const res = await fetch('/api/auth/login', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						username: username,
						password: passwordInput.trim()
					})
				});

				if (!res.ok) {
					throw new Error('密碼錯誤');
				}

				updateLockState(false);
				resetLockTimer();
				toast.success('🔓 解鎖成功！');
				return;
			} catch (err) {
				toast.error('密碼不正確，解鎖失敗');
				throw err;
			}
		}

		// ==========================================
		// 2. 指紋解鎖路徑（與 LoginPage.tsx 完全一致）
		// ==========================================
		try {
			// 檢查本地是否有 device_key
			const storageKey = `device_key_${userId}`;
			const deviceKey = localStorage.getItem(storageKey);
			if (!deviceKey) {
				toast.error('本裝置尚未建立裝置金鑰，請使用密碼解鎖');
				throw new Error('無裝置金鑰');
			}

			// 步驟 A: 帶入 username 獲取驗證選項
			const optionsRes = await fetch(`/api/auth/webauthn-login?username=${encodeURIComponent(username)}`);
			if (!optionsRes.ok) {
				const err = await optionsRes.json();
				throw new Error(err.error || '獲取登錄選項失敗');
			}

			const options = await optionsRes.json();

			// 步驟 B: 觸發系統指紋辨識視窗 👈 【跳出指紋】
			const attestation = await startAuthentication({ optionsJSON: options });

			// 步驟 C: 驗證指紋
			const verifyRes = await fetch('/api/auth/webauthn-login', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					userId: options.userId || userId,
					attestation
				})
			});

			const data = await verifyRes.json();
			if (!verifyRes.ok) {
				throw new Error(data.error || '指紋驗證失敗');
			}

			// 驗證成功，解鎖
			updateLockState(false);
			resetLockTimer();
			toast.success('🔓 指紋驗證成功，已解鎖！');

		} catch (err) {
			console.error('指紋解鎖失敗:', err);
			const msg = err instanceof Error ? err.message : '指紋辨識失敗';
			toast.error(msg);
			throw err;
		}
	};

  // ==========================================
  // 登入
  // ==========================================
  const handleLogin = async (
    id: string,
    name: string,
    passwordOrDeviceKey: string,
    encryptedDataKey: string,
    salt: string
  ) => {
    try {
      const encoder = new TextEncoder();
      
      const isDeviceKey = /^[A-Za-z0-9+/=]+$/.test(passwordOrDeviceKey) && passwordOrDeviceKey.length >= 40;
      
      let derivedKey: CryptoKey;

      if (isDeviceKey) {
        const baseKey = await crypto.subtle.importKey(
          'raw',
          encoder.encode(passwordOrDeviceKey),
          { name: 'PBKDF2' },
          false,
          ['deriveKey']
        );
        derivedKey = await crypto.subtle.deriveKey(
          {
            name: 'PBKDF2',
            salt: encoder.encode(salt),
            iterations: 100000,
            hash: 'SHA-256'
          },
          baseKey,
          { name: 'AES-GCM', length: 256 },
          false,
          ['decrypt']
        );
      } else {
        const keyMaterial = await crypto.subtle.importKey(
          'raw',
          encoder.encode(passwordOrDeviceKey),
          'PBKDF2',
          false,
          ['deriveKey']
        );
        derivedKey = await crypto.subtle.deriveKey(
          {
            name: 'PBKDF2',
            salt: encoder.encode(salt),
            iterations: 100000,
            hash: 'SHA-256'
          },
          keyMaterial,
          {
            name: 'AES-GCM',
            length: 256
          },
          false,
          ['decrypt']
        );
      }

      const binaryString = atob(encryptedDataKey);
      const combinedData = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        combinedData[i] = binaryString.charCodeAt(i);
      }

      const iv = combinedData.slice(0, 12);
      const encryptedDEK = combinedData.slice(12);

      const dek = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        derivedKey,
        encryptedDEK
      );

      const dekKey = await crypto.subtle.importKey(
        'raw',
        new Uint8Array(dek),
        { name: 'AES-GCM' },
        true,
        ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey']
      );

      // ==========================================
      // 儲存到 sessionStorage
      // ==========================================
      sessionStorage.setItem('auth_authenticated', 'true');
      sessionStorage.setItem('auth_userId', id);
      sessionStorage.setItem('auth_username', name);
      sessionStorage.setItem('auth_masterPassword', passwordOrDeviceKey);
      sessionStorage.setItem('auth_encryptedDataKey', encryptedDataKey);
      sessionStorage.setItem('auth_salt', salt);
      
      // ✅ 分離鎖屏密碼
      if (isDeviceKey) {
        // 指紋登入：使用之前儲存的鎖屏密碼
        const savedLockPassword = sessionStorage.getItem('auth_lockPassword');
        if (savedLockPassword) {
          setLockPassword(savedLockPassword);
        } else {
          // 如果沒有鎖屏密碼，提示使用者
          // toast.error('請先使用密碼登入一次以設定鎖屏密碼');
          setLockPassword('');
        }
      } else {
        // 密碼登入：儲存使用者密碼作為鎖屏密碼
        setLockPassword(passwordOrDeviceKey);
        sessionStorage.setItem('auth_lockPassword', passwordOrDeviceKey);
      }
      
      // 序列化並儲存 DEK
      const dekBase64 = await serializeCryptoKey(dekKey);
      if (dekBase64) {
        sessionStorage.setItem('auth_dek', dekBase64);
      }

      setUserId(id);
      setUsername(name);
      setMasterPassword(passwordOrDeviceKey);
      setDataEncryptionKey(dekKey);
      setIsAuthenticated(true);
      setIsLocked(false);

      console.log('✅ 登入成功！', isDeviceKey ? '使用 Device Key' : '使用密碼');

    } catch (error) {
      console.error('登入失敗:', error);
      throw new Error('登入失敗，請重試');
    }
  };

  // ==========================================
  // 登出
  // ==========================================
  const handleLogout = () => {
    if (clearNotifiedIds) {
      clearNotifiedIds();
    }
    
    sessionStorage.removeItem('auth_authenticated');
    sessionStorage.removeItem('auth_userId');
    sessionStorage.removeItem('auth_username');
    sessionStorage.removeItem('auth_masterPassword');
    sessionStorage.removeItem('auth_encryptedDataKey');
    sessionStorage.removeItem('auth_salt');
    sessionStorage.removeItem('auth_dek');
    sessionStorage.removeItem('auth_lockPassword');

    setIsAuthenticated(false);
    setIsLocked(false);
    setUserId('');
    setUsername('');
    setMasterPassword('');
    setLockPassword('');
    setDataEncryptionKey(null);
    setSearchQuery('');
    setIsSettingsOpen(false);
    if (lockTimerRef.current) {
      clearTimeout(lockTimerRef.current);
    }
  };

  // ==========================================
  // 其他處理函數
  // ==========================================
  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
    resetLockTimer();
  };

  const handlePasswordChanged = () => {
    setIsSettingsOpen(false);
    toast.success('密碼已修改，請重新登入');
    setTimeout(() => {
      handleLogout();
    }, 300);
  };

  const handleCreateNote = () => {
    setEditingNote(null);
    setIsEditorOpen(true);
    resetLockTimer();
  };

  const handleEditNote = (note: any) => {
    setEditingNote(note);
    setIsEditorOpen(true);
    resetLockTimer();
  };

	const handleShareNote = (note: any) => {
		console.log('🔍 handleShareNote 被呼叫');
		console.log('🔍 note:', note);
		console.log('🔍 note?.id:', note?.id);
		console.log('🔍 note?.noteKey:', note?.noteKey);
		console.log('🔍 note 類型:', typeof note);
		
		setShareNote(note);
		setIsShareDialogOpen(true);
		
		// ✅ 加入微延遲，確保 state 更新
		setTimeout(() => {
			console.log('🔍 shareNote 更新後:', shareNote);
		}, 100);
	};
	
  const handleFilterType = (type: string | null) => {
    setFilterType(type);
    if (type === 'memo' || type === 'password' || type === 'todo') {
      setSelectedType(type);
    } else {
      setSelectedType('memo');
    }
    resetLockTimer();
  };

  const handleSaveNote = async (
    id: string,
    title: string,
    content: string,
    category: string,
    tags: string[],
    images: string[],
    type: NoteType,
    username?: string,
    password?: string,
    due_date?: string,
    status?: string
  ) => {
    if (id === 'new') {
      await createNote({ title, content, category, tags, images, type, username, password, due_date, status });
    } else {
      await updateNote(id, { title, content, category, tags, images, type, username, password, due_date, status });
    }
    resetLockTimer();
  };

  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setEditingNote(null);
    resetLockTimer();
  };

  const getFilteredAndSearchedNotes = () => {
    let result = notes;
    
    if (filterType) {
      result = result.filter(n => n.type === filterType);
    }
    
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(note => {
        if (note.title.toLowerCase().includes(q)) return true;
        if (note.content.toLowerCase().includes(q)) return true;
        if (note.tags.some(tag => tag.toLowerCase().includes(q))) return true;
        return false;
      });
    }
    
    return result;
  };

  const filteredAndSearchedNotes = getFilteredAndSearchedNotes();

  // ==========================================
  // ✅ 渲染
  // ==========================================
  
  // 恢復中
  if (isRestoring) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-gray-500 dark:text-gray-400 text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent mb-4"></div>
          <p>恢復登入狀態...</p>
        </div>
      </div>
    );
  }

  // 未登入
  if (!isAuthenticated) {
    return (
      <>
        <LoginPage onLogin={handleLogin} />
        <Toaster position="top-center" />
      </>
    );
  }

  // ==========================================
  // ✅ 已登入：永遠渲染主畫面
  // ==========================================
  return (
    <>
      {/* ✅ 主畫面永遠存在，不會被卸載 */}
      <MainLayout
				currentUserId={userId}
        onLogout={handleLogout}
        onCreateNote={handleCreateNote}
        onEditNote={handleEditNote}
        onShareNote={handleShareNote}
        onFilterType={handleFilterType}
        currentFilter={filterType}
        username={username}
        allNotes={notes}
        notes={filteredAndSearchedNotes}
        loading={loading}
        deleteNote={deleteNote}
        togglePin={togglePin}
        toggleArchive={toggleArchive}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchResultCount={filteredAndSearchedNotes.length}
        onOpenSettings={handleOpenSettings}
        receivedTotal={receivedTotal}
        receivedAccepted={receivedAccepted}
        receivedPendingCount={receivedPendingCount}
        myShareCount={myShareCount}
        myAcceptedCount={myAcceptedCount}
        sharedPendingCount={sharedPendingCount}
      />
			console.log('當前登入 userId:', userId); // 👈 觀察這裡是否有值
      <SettingsModal
        isOpen={isSettingsOpen}
        userId={userId}
        username={username}
        masterPassword={masterPassword}
        currentDEK={dataEncryptionKey}
        decryptedDEK={dataEncryptionKey}
        onClose={() => setIsSettingsOpen(false)}
        onPasswordChanged={handlePasswordChanged}
        onImportSuccess={() => loadNotes()}
      />

      <NoteEditor
        isOpen={isEditorOpen}
        note={editingNote}
        defaultType={selectedType}
        onSave={handleSaveNote}
        onClose={handleCloseEditor}
      />

      {/* 分享對話框 */}
			{/* ✅ 確保 shareNote 存在 */}
			{isShareDialogOpen && shareNote && (
				<ShareDialog
					isOpen={isShareDialogOpen}
					note={shareNote}
					userId={userId}
					dek={dataEncryptionKey}
					onClose={() => {
						setIsShareDialogOpen(false);
						setShareNote(null);
					}}
					onShareSuccess={() => {
						setIsShareDialogOpen(false);
						setShareNote(null);
						refresh();
					}}
				/>
			)}

      {/* 分享列表彈窗 */}
      {isShareListOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[150] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                📤 分享
              </h3>
              <button
                onClick={() => setIsShareListOpen(false)}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              <ShareList
                userId={userId}
                initialTab={shareListTab}
                onViewNote={handleViewSharedNote}
                dek={dataEncryptionKey}
                onShareAccepted={() => {
                  loadNotes();
                  refresh();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* SharedNoteViewer */}
      <SharedNoteViewer
        isOpen={!!viewingNoteId}
        noteId={viewingNoteId}
        userId={userId}
        decryptedDEK={dataEncryptionKey} 
        onClose={() => setViewingNoteId(null)}
      />

			{/* 鎖屏遮罩 */}
			{isLocked && (
				<div className="fixed inset-0 z-[9999]">
					<UnlockPage userId={userId} onUnlock={handleUnlock} />
				</div>
			)}

      <Toaster position="top-center" />
    </>
  );
}

export default App;